import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import {
    createRandom,
    gsap,
    prefersReducedMotion,
    useGSAP
} from '@/lib/motion'
import Sparkle from '@/components/landing/decor/sparkle'

interface Particle {
    kind: 'star' | 'dot'
    x: number
    y: number
    size: number
    speed: number
    gold: boolean
    opacity: number
    delay: number
    mobile: boolean
}

const PARTICLES: Array<Particle> = (() => {
    const rand = createRandom(812)

    return Array.from({ length: 28 }, (_, index): Particle => {
        const kind = index % 3 === 2 ? 'dot' : 'star'

        return {
            kind,
            x: Math.round(rand() * 1000) / 10,
            y: rand(),
            size: kind === 'star' ? 6 + Math.round(rand() * 10) : 3 + Math.round(rand() * 5),
            speed: 0.05 + rand() * 0.38,
            gold: rand() > 0.3,
            opacity: 0.35 + Math.round(rand() * 50) / 100,
            delay: Math.round(rand() * 40) / 10,
            mobile: index < 12
        }
    })
})()

// ✦ stars and specks of light drifting through the whole page at their own depth, so they slide
// past at different speeds as the visitor scrolls. They grow quiet once the contact ending arrives.
const StarField: FC = (): ReactNode => {
    const layerRef = useRef<HTMLDivElement>(null)
    const innerRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        const layer = layerRef.current
        const inner = innerRef.current

        if (!layer || !inner) return

        const reduce = prefersReducedMotion()
        const nodes = gsap.utils.toArray<HTMLElement>('[data-particle]', inner)
        const setters = nodes.map(node => gsap.quickSetter(node, 'y', 'px') as (value: number) => void)

        let lastScroll = Number.NaN
        let lastHeight = 0

        const update = (): void => {
            const scroll = window.scrollY
            const height = window.innerHeight + 120

            if (scroll === lastScroll && height === lastHeight) return

            lastScroll = scroll
            lastHeight = height

            for (let index = 0; index < setters.length; index++) {
                const particle = PARTICLES[index]
                const raw = particle.y * height - scroll * particle.speed

                setters[index]((((raw % height) + height) % height) - 60)
            }
        }

        update()

        if (!reduce) gsap.ticker.add(update)

        gsap.to(layer, { autoAlpha: 1, duration: 2.4, delay: reduce ? 0 : 4, ease: 'sine.out' })

        const contact = document.getElementById('contact')

        if (contact) {
            gsap.fromTo(inner, { autoAlpha: 1 }, {
                autoAlpha: 0.06,
                ease: 'none',
                scrollTrigger: {
                    trigger: contact,
                    start: 'top 80%',
                    end: 'top 15%',
                    scrub: true
                }
            })
        }

        return () => gsap.ticker.remove(update)
    }, { scope: layerRef })

    return (
        <div ref={layerRef} aria-hidden='true' className='pointer-events-none fixed inset-0 z-20 overflow-hidden opacity-0'>
            <div ref={innerRef} className='absolute inset-0'>
                {PARTICLES.map((particle, index) => (
                    <span
                        key={index}
                        data-particle
                        className={cn('absolute top-0 block', !particle.mobile && 'hidden lg:block')}
                        style={{ left: `${particle.x}%` }}
                    >
                        {particle.kind === 'star'
                            ? (
                                <Sparkle
                                    className={cn('block motion-safe:animate-twinkle', particle.gold ? 'text-gold' : 'text-white')}
                                    style={{
                                        width: particle.size,
                                        height: particle.size,
                                        opacity: particle.opacity,
                                        animationDelay: `${particle.delay}s`
                                    }}
                                />
                            )
                            : (
                                <span
                                    className='block rounded-full bg-[radial-gradient(circle,rgba(255,252,240,0.95),rgba(255,252,240,0)_70%)]'
                                    style={{
                                        width: particle.size * 2,
                                        height: particle.size * 2,
                                        opacity: particle.opacity
                                    }}
                                />
                            )}
                    </span>
                ))}
            </div>
        </div>
    )
}

export default StarField
